import {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Background,
  Controls,
  Edge,
  MiniMap,
  Node,
  NodeTypes,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ConditionNode,
  DefinitionPane,
  InternalNode,
  InternalNode as InternalNodeType,
  LeafNode,
  LogicalOperator,
  Outcome,
  PaneType,
  PaneTypes,
  Representation,
  RulePane,
  TruthValue,
} from "@unlikelyai-magic/shared/data-access/api";
import { cn } from "@unlikelyai-magic/utils";
import { getLayoutedElements } from "./layout";
import { AutomaticEditConditionNode } from "./nodes/AutomaticEditConditionNode";
import { BlurbNode } from "./nodes/BlurbNode";
import { ConditionNode as ConditionNodeComponent } from "./nodes/ConditionNode";
import { ConditionNodeProps } from "./nodes/ConditionNode";
import { CustomEdge } from "./nodes/CustomEdge";
import { EditedConditionNode } from "./nodes/EditedConditionNode";
import { GroupNode, GroupNodeProps } from "./nodes/GroupNode";
import { LogicalConnectiveNode } from "./nodes/LogicalConnectiveNode";
import {
  conditionNodeToRepresentation,
  RFNodeData,
  convertToRF,
} from "./utils";

export interface LeafNodeValue {
  productTreeId: string;
  id: string;
  value: Outcome;
}

export const edgeTypes = {
  "custom-edge": CustomEdge,
};

/**
 * Default node types used by LayoutFlow.
 * Can be overridden via the nodeTypes prop.
 */
export const defaultNodeTypes = {
  connective: LogicalConnectiveNode,
  condition: ConditionNodeComponent,
  blurb: BlurbNode,
  group: GroupNode,
  edited: EditedConditionNode,
  automaticEditNode: AutomaticEditConditionNode,
};

/**
 * Type for custom node types that can override the defaults.
 * Uses ComponentType to accept both regular and memoized components.
 */
export interface CustomNodeTypes {
  connective?: ComponentType<{
    id: string;
    data: RFNodeData;
    isConnectable: boolean;
  }>;
  condition?: ComponentType<ConditionNodeProps>;
  blurb?: ComponentType<{ id: string; data: RFNodeData }>;
  group?: ComponentType<GroupNodeProps>;
}

export interface LayoutFlowProps {
  panes: PaneTypes[];
  value?: LeafNodeValue[];
  className?: string;
  showMiniMap?: boolean;
  /**
   * Optional callback when a node is clicked.
   * Receives the node ID, node type, and pane type (for group nodes).
   */
  onNodeClick?: (nodeId: string, nodeType: string, paneType?: PaneType) => void;
  /**
   * Optional callback when the pane (background) is clicked.
   */
  onPaneClick?: () => void;
  /**
   * Optional callback to hydrate triggers from panes.
   * This is called with the assignments and explanations extracted from rule panes.
   */
  onHydrateTriggers?: (data: {
    leaves: Record<string, TruthValue>;
    explanations: Record<string, string>;
  }) => void;
  /**
   * Optional custom node types. If provided, will be merged with default node types.
   * Use this to override specific node components (e.g., to add Redux integration).
   */
  nodeTypes?: CustomNodeTypes;
}

export const LayoutFlow = ({
  panes,
  value,
  className,
  showMiniMap = false,
  onNodeClick: onNodeClickProp,
  onPaneClick: onPaneClickProp,
  onHydrateTriggers,
  nodeTypes: customNodeTypes,
}: LayoutFlowProps) => {
  useEffect(() => {
    if (onHydrateTriggers) {
      hydrateTriggers(panes, onHydrateTriggers);
    }
  }, [panes, onHydrateTriggers]);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    return panes
      .filter(
        (pane) =>
          pane.paneType === PaneType.RULE ||
          pane.paneType === PaneType.DEFINITION
      )
      .reduce(
        (acc: { nodes: Node[]; edges: Edge[] }, pane: PaneTypes) => {
          return convertPaneToRF(pane, acc);
        },
        { nodes: [], edges: [] }
      );
  }, [panes]);
  const { fitView } = useReactFlow();

  const [isInitialLayout, setIsInitialLayout] = useState(false);
  const isInitialized = useNodesInitialized();

  // Merge custom node types with defaults and cast to NodeTypes for ReactFlow
  const nodeTypes = useMemo(
    () =>
      ({
        ...defaultNodeTypes,
        ...customNodeTypes,
      }) as NodeTypes,
    [customNodeTypes]
  );
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges);

  const updateNodeInternals = useUpdateNodeInternals();
  const { updateNodeData } = useReactFlow();

  useEffect(() => {
    if (!isInitialized) return;
    if (!nodes.length) return;
    if (!value?.length) {
      nodes.forEach((n) => {
        if (n.data.value == Outcome.UNDECIDED) return;
        updateNodeData(n.id, {
          value: Outcome.UNDECIDED,
        });
      });
      return;
    }
    const nodeMap = new Map<string, Node>();
    nodes.forEach((n) => {
      nodeMap.set(`${n.data.productTreeId}-${n.data.label}`, n);
    });
    value.forEach((v) => {
      const node = nodeMap.get(`${v.productTreeId}-${v.id}`);
      if (!node) return;
      updateNodeData(node.id, {
        value: v.value,
      });
    });
  }, [isInitialized, value]);

  const onLayout = useCallback(
    async (
      nodes: Node<RFNodeData>[],
      edges: Edge[],
      direction: "TB" | "LR"
    ) => {
      const layouted = await getLayoutedElements(nodes, edges, { direction });
      updateNodeInternals(nodes.map((node: any) => node.id));

      setNodes(layouted.nodes);
      setEdges(layouted.edges);

      setTimeout(() => {
        fitView({
          duration: 1000,
          // Ensure all panes are visible and nicely centered on initial load.
          // Let React Flow determine the zoom level needed to fit everything.
          padding: 0.2,
        });
      }, 200);
    },
    [fitView, setEdges, setNodes, updateNodeInternals]
  );

  useEffect(() => {
    if (!isInitialLayout && isInitialized) {
      const timeoutId = setTimeout(async () => {
        await onLayout(nodes as Node<RFNodeData>[], edges, "TB");
        setIsInitialLayout(true);
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [isInitialLayout, isInitialized, onLayout]);

  const onConnect = useCallback(
    (params: any) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const zoomToNode = useCallback(
    (node: Node) => {
      fitView({
        nodes: [node],
        duration: 800,
        padding: 0.2,
        maxZoom: 1,
        minZoom: 0.1,
      });
    },
    [fitView]
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      // Call the optional callback if provided
      if (onNodeClickProp) {
        const paneType =
          node.type === "group" ? (node.data.type as PaneType) : undefined;
        onNodeClickProp(node.id, node.type ?? "", paneType);
      }
      // Zoom to the clicked node
      zoomToNode(node);
    },
    [onNodeClickProp, zoomToNode]
  );

  const onPaneClick = useCallback(() => {
    if (onPaneClickProp) {
      onPaneClickProp();
    }
    fitView({
      duration: 1000,
    });
  }, [onPaneClickProp, fitView]);

  return (
    <div className={cn("w-full h-full", className)}>
      <ReactFlow
        defaultViewport={{
          x: 0,
          y: 0,
          // Initial zoom.
          zoom: 0.25,
        }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        minZoom={0.01}
        style={{
          transition: "opacity 0.5s ease-in-out",
        }}
      >
        <Controls />
        {showMiniMap && <MiniMap />}
        <Background gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

const hydrateTriggers = (
  panes: PaneTypes[],
  onHydrateTriggers: (data: {
    leaves: Record<string, TruthValue>;
    explanations: Record<string, string>;
  }) => void
) => {
  const allAssignments = panes
    .filter((pane) => pane.paneType == PaneType.RULE)
    .map((pane) => (pane as RulePane).rule.evaluatedTrigger.assignment);
  const allExplanations = panes
    .filter((pane) => pane.paneType == PaneType.RULE)
    .map((pane) => (pane as RulePane).rule.evaluatedTrigger.explanations);
  const explanations = allExplanations.reduce<Record<string, string>>(
    (acc, explanations) => {
      return {
        ...acc,
        ...explanations,
      };
    },
    {}
  );
  const assignments = allAssignments.reduce<Record<string, TruthValue>>(
    (acc, assignment) => {
      return {
        ...acc,
        ...assignment,
      };
    },
    {}
  );
  onHydrateTriggers({ leaves: assignments, explanations: explanations });
};

/**
 * This method is called iteratively to build the React Flow nodes and edges from different pane types.
 * @returns Nodes and edges
 */
const convertPaneToRF = (
  pane: PaneTypes,
  acc: { nodes: Node[]; edges: Edge[] }
) => {
  const baseGroupElements = {
    type: "group",
    position: { x: 0, y: 0 },
  };
  const sanitizedId = pane.id;
  switch (pane.paneType) {
    case PaneType.RULE: {
      const rulePane = pane as RulePane;
      const ruleGroupNode = {
        id: sanitizedId,
        data: {
          productTreeId: rulePane.rule.name,
          type: pane.paneType,
          paneId: "",
          blurbInformation: "",
          hasTree: true,
        },
        ...baseGroupElements,
      };
      const rootNodeTrigger = rulePane.rule.evaluatedTrigger.rootNode;
      const rootNodeObligations = rulePane.rule.obligations.tree.rootNode;
      const splicedRootNode = {
        name: rulePane.rule.name,
        question: rulePane.rule.name,
        logicalOperator: LogicalOperator.AND,
        subConditions: [rootNodeTrigger, rootNodeObligations],
        sourceRules: [],
      };
      const { nodes: triggerNodes, edges: triggerEdges } = convertToRF(
        sanitizedId,
        conditionNodeToRepresentation(splicedRootNode)
      );
      return {
        nodes: [ruleGroupNode, ...acc.nodes, ...triggerNodes],
        edges: [...acc.edges, ...triggerEdges],
      };
    }
    case PaneType.DEFINITION: {
      const definitionPane = pane as DefinitionPane;
      const definitionGroupNode = {
        id: sanitizedId,
        data: {
          productTreeId: definitionPane.definition.name,
          type: pane.paneType,
          paneId: pane.id,
          blurbInformation: blurbInformationMap.get(pane.paneType),
          hasTree: true,
        },
        ...baseGroupElements,
      };
      const rootNode = definitionPane.definition.rootNode;
      const { nodes: definitionNodes, edges: definitionEdges } =
        (rootNode as InternalNodeType).subConditions?.length > 0
          ? convertToRF(
              sanitizedId,
              conditionNodeToRepresentation(
                rootNode,
                definitionPane.explanation
              )
            )
          : convertToRF(
              sanitizedId,
              conditionNodeToRepresentation({
                name: definitionPane.explanation,
                question: definitionPane.explanation,
                logicalOperator: LogicalOperator.AND,
                subConditions: [rootNode],
                sourceRules: [],
              })
            );
      return {
        nodes: [definitionGroupNode, ...acc.nodes, ...definitionNodes],
        edges: [...acc.edges, ...definitionEdges],
      };
    }
    default: {
      return { nodes: [], edges: [] };
    }
  }
};

const blurbInformationMap = new Map<PaneType, string>([
  [
    PaneType.RULE,
    "Rules are descriptions of a person or company’s obligations in a certain situation. We extract these and then analyse them to determine how the application is required to behave at runtime. Extracting a rule creates two decision trees: a ‘trigger’ and an ‘obligation’. The trigger describes who the rule applies to (eg, “A firm designing targeted support frameworks”). The obligation describes what the person or company has to do (“the firm must have the consumer’s best interest at heart”).",
  ],
  [
    PaneType.DEFINITION,
    "Definitions are detailed descriptions of what is included in certain key regulatory topics. For example, the FCA defines in a lot of detail what counts as advice and what is merely information. These paragraphs are analysed and extracted into a decision tree. This tree can be referenced by other definitions; for example, the definition of a personalised recommendation references the definition of advice. They can also be referenced by rules; for example, there is a rule that only an appropriately authorised person can give advice.",
  ],
  [PaneType.COMPILED_RULE, "I am compiled runtime rule"],
  [PaneType.PROHIBITION, "I am prohibition"],
  [
    PaneType.EXAMPLE,
    "In some regulatory documents, the FCA provides explicit examples of what they do or don’t consider to be included in a definition. For example, their definition of advice is followed by several examples of advice and several examples of information. These examples are detected by the ingestion pipeline, categorised and stored alongside the definitions of the relevant topics. They are used to increase accuracy when the definition of a concept is evaluated.",
  ],
  [
    PaneType.GUIDANCE,
    "In some regulatory documents, the FCA provides generally phrased guidance about what ‘may’ or ‘may not’ be included in a certain definition or rule. This content is useful for grounding the interpretation of the material and for resolving edge cases in the evaluation. This form of guidance is detected by the ingestion pipeline, categorised and stored alongside the definitions of the relevant topics. It is used to increase accuracy when the definition of a concept is evaluated.",
  ],
  [
    PaneType.NOT_APPLICABLE,
    "This content was analysed by the ingestion pipeline and was not found to contain valuable information of any of the types mentioned (rules, definitions, examples, guidance or context).",
  ],
  [
    PaneType.CONTEXT,
    "Particularly in the opening material of a chapter or section, regulatory documents often provide contextual statements establishing the purpose or relevance of the following material. This content is useful for interpreting the rules which follow in the correct way. This context is detected by the ingestion pipeline, categorised and stored alongside the rules in the section which follow. It is used to increase accuracy when the rules are evaluated to determine whether they imply a runtime obligation for our use case.",
  ],
]);
